import { BookingSummary } from 'Views/Components/_HumanWritten/BookingSummaryDto';
import { BookingEntity } from 'Models/Entities';
import { BookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';
import { TabSelected } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardTypes';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../TypeGuards';
import { getJourneyChangeBlockedReason } from './BookingChangePermissions';
import { getInnerWizardData } from './FerryBookingToWizardDataUtils';
import { getStopById } from '../../../Global/JourneyElements/Utils/getStopById';

export function convertBookedBookingToWizardData(
	booking: BookingEntity,
	alteration?: boolean,
	alterReturn: boolean = true,
) {
	const bookingIsDepartureOfTwoWayBooking = isNullOrUndefined(booking.returnBookingFor);

	let departureBooking: BookingEntity = booking;
	let returnBooking: BookingEntity | null = null;
	let returnSummary: BookingSummary | null = null;
	let departureSummary = departureBooking.bookedSummary;

	// This should never happen, but just in case
	if (isNullOrUndefined(departureSummary)) {
		console.error('Booked summary missing, this should not happen');
		return new BookingWizardData({});
	}

	if (alteration && alterReturn) {
		returnBooking = booking.returnBooking ? booking.returnBooking : booking.returnBookingFor;
		if (bookingIsDepartureOfTwoWayBooking) {
			returnSummary = returnBooking?.bookedSummary ?? null;
		} else {
			departureBooking = returnBooking;
			returnBooking = booking;
			departureSummary = departureBooking.bookedSummary;
			returnSummary = returnBooking.bookedSummary ?? null;
		}
	}

	return convertBookingToWizardData(
		booking,
		departureBooking,
		returnBooking,
		departureSummary!,
		returnSummary,
		alteration,
		alterReturn,
	);
}

export function convertBookingToWizardDataUsingRecentSummary(
	booking: BookingEntity,
	alteration?: boolean,
	alterReturn: boolean = true,
) {
	const bookingIsDepartureOfTwoWayBooking =
		(isNotNullOrUndefined(booking.returnBooking) && isNullOrUndefined(booking.returnBookingFor)) ||
		(isNullOrUndefined(booking.returnBooking) && isNullOrUndefined(booking.returnBookingFor));

	let departureBooking: BookingEntity = booking;
	let returnBooking: BookingEntity | null = null;
	let returnSummary: BookingSummary | null = null;
	let departureSummary = departureBooking.bookingSummaries.recentSummary;

	if (alteration || alterReturn) {
		returnBooking = booking.returnBooking ? booking.returnBooking : booking.returnBookingFor;
		if (bookingIsDepartureOfTwoWayBooking) {
			returnSummary = returnBooking?.bookingSummaries?.recentSummary;
		} else {
			departureSummary = returnBooking?.bookingSummaries?.recentSummary;
			departureBooking = returnBooking;
			// The viewed booking's own summary, not the one just assigned to `departureSummary`. Both
			// names pointed at the outbound leg, so a booking opened from its return half described
			// that leg twice — and the returning fare types and seats folded in below would have been
			// the departing ones. `convertBookedBookingToWizardData` above has always read the two
			// separately; this path simply never did.
			returnSummary = booking.bookingSummaries?.recentSummary ?? null;
			returnBooking = booking;
		}
	}

	return convertBookingToWizardData(
		booking,
		departureBooking,
		returnBooking,
		departureSummary!,
		returnSummary,
		alteration,
		alterReturn,
	);
}

export function convertBookingToWizardData(
	booking: BookingEntity,
	departureBooking: BookingEntity,
	returnBooking: BookingEntity | null,
	departureSummary: BookingSummary,
	returnSummary: BookingSummary | null,
	alteration?: boolean,
	alterReturn: boolean = true,
) {
	// If the user is passing in a one way trip, we still need to know the associated ferry trip departure date
	// so that we can let the user know when the ticket is before/after the assoicated departure/return ticket
	// E.g. a user should not be able to book a departure ticket
	// that departs after the return ticket and likewise a user should not be able to book a return ticket
	// that has a departure time after the associated departure ticket time.
	const departureTicketDate = departureSummary.ferryTrip.startDate;
	const returnTicketDate = returnSummary?.ferryTrip.startDate;

	// This will let the wizard know which date is the associated booking dateTime
	const associatedDepartureDateTime = returnSummary?.ferryTrip?.departureDateTime ?? undefined;

	const departureCancellationFee = booking.cancellationFee;
	const departureCancellationCutoffHours = booking.cancellationCutoffHours;
	const returnCancellationFee = returnBooking?.cancellationFee;

	const bookingToEditId = departureBooking.id;
	let tabSelected = 'passenger';
	if (departureSummary.cargoInfo) {
		tabSelected = 'vehicle';
	} else if (departureSummary.freightDetail) {
		tabSelected = 'freight';
	}

	const startStopLocationId = getStopById(departureSummary?.startStopId)?.locationId;
	const endStopLocationId = getStopById(departureSummary?.endStopId)?.locationId;

	const tripType = returnSummary && alterReturn ? 'return' : 'one way';

	let restrictedMode = false;
	if ((departureSummary.discountAmount ?? 0) > 0 || (returnSummary?.discountAmount ?? 0) > 0) {
		restrictedMode = true;
	}

	return new BookingWizardData({
		userId: booking.userId,
		tabSelected: tabSelected as TabSelected,
		wizardMode: alteration ? 'ALTERATION' : 'CREATE',
		restrictedMode: restrictedMode,
		// Asked of both legs, not of `booking`: the caller swaps departureBooking/returnBooking when
		// the viewed booking is the return half, and an edit applied to both trips submits both. The
		// backend guard is per-leg, so a return leg on the check-in ladder would otherwise get all
		// the way to a 400. Same derivation the edit modal uses, so the two cannot disagree.
		journeyChangeBlockedReason: getJourneyChangeBlockedReason(departureBooking, returnBooking),
		departureTrip:
			!alteration || alterReturn
				? undefined
				: isNotNullOrUndefined(booking.returnBooking) || isNullOrUndefined(booking.returnBookingFor),

		// Feature: Multi-stop - summary to cart fields
		// Add departingJourney if departing trip is available
		...(departureSummary && {
			departingJourney: {
				tripId: departureSummary.ferryTrip.id,
				startStopId: departureSummary.startStopId,
				endStopId: departureSummary.endStopId,
				// Normally set by the tickets step. An edit that starts past it (add-ons, fare type or
				// seat) never renders that step, and the add-on cards then fell back to today's date.
				departureDateTime: departureSummary.tripDateTime ?? departureSummary.ferryTrip.departureDateTime,
			},
		}),
		// Add returningJourney if returning trip is available
		...(returnSummary && {
			returningJourney: {
				tripId: returnSummary.ferryTrip.id,
				startStopId: returnSummary.startStopId,
				endStopId: returnSummary.endStopId,
				departureDateTime: returnSummary.tripDateTime ?? returnSummary.ferryTrip.departureDateTime,
			},
		}),

		departureTicketId: departureSummary?.ferryTrip?.id,
		returningTicketId: isNotNullOrUndefined(returnSummary) && alterReturn ? returnSummary?.ferryTrip?.id : '',

		associatedTripDateTime: alteration && !alterReturn ? associatedDepartureDateTime : undefined,
		automaticallyApplyAddOnsToBothDirections: tripType === 'return',
		disabledVehicleType: false,
		bookingToEdit: bookingToEditId,
		// The homeward leg's own booking. `bookingToEditId` above is the outbound one, and the two
		// sailings each have to be asked about their own — see the field's note.
		returnBookingToEdit: returnBooking?.id,
		tripType: tripType,
		fromLocationId: startStopLocationId,
		toLocationId: endStopLocationId,
		startDate: departureTicketDate,
		ticketSelectionStartDate: departureTicketDate,
		endDate: returnTicketDate ?? departureTicketDate,
		ticketSelectionEndDate: returnTicketDate ?? departureTicketDate,

		// Add departingJourney if departing trip is available
		...(departureSummary && {
			departingJourney: {
				tripId: departureSummary.ferryTrip.id,
				startStopId: departureSummary.startStopId,
				endStopId: departureSummary.endStopId,
				// Normally set by the tickets step. An edit that starts past it (add-ons, fare type or
				// seat) never renders that step, and the add-on cards then fell back to today's date.
				departureDateTime: departureSummary.tripDateTime ?? departureSummary.ferryTrip.departureDateTime,
			},
		}),

		// Add returningJourney if returning trip is available
		...(returnSummary && {
			returningJourney: {
				tripId: returnSummary.ferryTrip.id,
				startStopId: returnSummary.startStopId,
				endStopId: returnSummary.endStopId,
				departureDateTime: returnSummary.tripDateTime ?? returnSummary.ferryTrip.departureDateTime,
			},
		}),

		innerWizardData: getInnerWizardData(departureSummary, alterReturn, alteration ?? false, returnSummary),
		acceptedTsAndCs: false,

		// Fees
		departingCancellationFee: departureCancellationFee,
		returningCancellationFee: returnCancellationFee,
		departingCancellationCutoffHours: departureCancellationCutoffHours,
	});
}
